import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import superAdminAPI from '../services/superAdminAPI';
import SchoolCreationModal from '../Components/SchoolCreationModal';
import SchoolEditModal from '../Components/SchoolEditModal';
import AssignToGroupModal from '../Components/AssignToGroupModal';
import {
    AlertTriangle,
    Building2,
    Calendar,
    CheckCircle,
    CheckSquare,
    Clock,
    Edit,
    Eye,
    GitBranch,
    GitMerge,
    Layers,
    Loader,
    MoreVertical,
    Pause,
    Plus,
    Power,
    RefreshCw,
    Search,
    Shield,
    Square,
    Trash2,
    Users,
    XCircle
} from 'lucide-react';
import { Toast } from '../Components/Toast'; // Add this component for notifications

const SchoolsManagement = () => {
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [subscriptionFilter, setSubscriptionFilter] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(null);
    const [confirmDeleteSchool, setConfirmDeleteSchool] = useState(null);
    const [schoolMenuOpen, setSchoolMenuOpen] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Multi-select & assign-to-group state
    const [selectMode, setSelectMode] = useState(false);
    const [selectedSchools, setSelectedSchools] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignModalSchools, setAssignModalSchools] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        // First check authentication
        if (!superAdminAPI.isAuthenticated()) {
            navigate('/?expired=true');
            return;
        }

        fetchSchools();
    }, [statusFilter, subscriptionFilter, navigate]);

    const fetchSchools = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                limit: 50
            };

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            const data = await superAdminAPI.getSchools(params);
            setSchools(data);
        } catch (error) {
            console.error("Error fetching schools:", error);
            const errorMessage = superAdminAPI.handleError(error);
            setError(errorMessage);

            // If session expired, redirect to login
            if (errorMessage.includes('Session expired')) {
                navigate('/?expired=true');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (schoolId, newStatus) => {
        try {
            setActionInProgress(schoolId);

            // Format the request body according to the backend expectations
            // For SchoolStatus enum, it needs to be wrapped in an object with a status field
            const statusData = { status: newStatus };

            await superAdminAPI.updateSchool(schoolId, statusData);
            showToast(`School status updated successfully`, 'success');

            fetchSchools(); // Refresh the list
        } catch (error) {
            console.error(`Error changing school status to ${newStatus}:`, error);
            const errorMessage = superAdminAPI.handleError(error);
            setError(errorMessage);
            showToast(`Failed to update school status: ${errorMessage}`, 'error');
        } finally {
            setActionInProgress(null);
        }
    };

    const handleDeleteSchool = async (schoolId) => {
        try {
            setActionInProgress(schoolId);

            await superAdminAPI.deleteSchool(schoolId);
            setConfirmDeleteSchool(null);
            showToast('School deleted successfully', 'success');
            fetchSchools(); // Refresh the list
        } catch (error) {
            console.error("Error deleting school:", error);
            const errorMessage = superAdminAPI.handleError(error);
            setError(errorMessage);
            showToast(`Failed to delete school: ${errorMessage}`, 'error');
        } finally {
            setActionInProgress(null);
        }
    };

    const handleSchoolCreated = (newSchool) => {
        setShowCreateModal(false);
        showToast(`School "${newSchool.name}" created successfully`, 'success');
        fetchSchools(); // Refresh the schools list
    };

    const handleEditSchool = (school) => {
        setSelectedSchool(school);
        setShowEditModal(true);
    };

    const handleSchoolUpdated = (updatedSchool) => {
        setShowEditModal(false);
        showToast(`School "${updatedSchool.name}" updated successfully`, 'success');
        fetchSchools(); // Refresh the schools list
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    // ── Multi-select helpers ──────────────────────────────────────────────────
    const toggleSelectMode = () => {
        setSelectMode((prev) => !prev);
        setSelectedSchools([]);
    };

    const toggleSchoolSelection = (school) => {
        // Only single-type schools can be selected
        if (school.school_type !== 'single') return;
        setSelectedSchools((prev) =>
            prev.some((s) => s.id === school.id)
                ? prev.filter((s) => s.id !== school.id)
                : [...prev, school]
        );
    };

    const isSchoolSelected = (schoolId) => selectedSchools.some((s) => s.id === schoolId);

    // Open modal for bulk assign (from action bar)
    const handleBulkAssignToGroup = () => {
        if (selectedSchools.length === 0) return;
        setAssignModalSchools(selectedSchools);
        setShowAssignModal(true);
    };

    // Open modal for single card assign (from card dropdown)
    const handleSingleAssignToGroup = (school) => {
        setSchoolMenuOpen(null);
        setAssignModalSchools([school]);
        setShowAssignModal(true);
    };

    const handleAssignSuccess = () => {
        setShowAssignModal(false);
        setAssignModalSchools([]);
        setSelectedSchools([]);
        setSelectMode(false);
        fetchSchools();
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} />, text: 'Active' },
            suspended: { color: 'bg-red-100 text-red-700', icon: <XCircle size={14} />, text: 'Suspended' },
            trial: { color: 'bg-yellow-100 text-yellow-700', icon: <Clock size={14} />, text: 'Trial' },
            inactive: { color: 'bg-gray-100 text-gray-700', icon: <AlertTriangle size={14} />, text: 'Inactive' }
        };

        const config = statusConfig[status] || statusConfig.inactive;

        return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.icon}
                {config.text}
            </span>
        );
    };

    const getSubscriptionBadge = (plan) => {
        const planConfig = {
            trial: { color: 'bg-blue-100 text-blue-700', text: 'Trial' },
            basic: { color: 'bg-purple-100 text-purple-700', text: 'Basic' },
            premium: { color: 'bg-orange-100 text-orange-700', text: 'Premium' },
            enterprise: { color: 'bg-indigo-100 text-indigo-700', text: 'Enterprise' }
        };

        const config = planConfig[plan] || planConfig.trial;

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.text}
            </span>
        );
    };

    // School type badge for single/group/branch
    const getSchoolTypeBadge = (schoolType) => {
        const typeConfig = {
            'single': { color: 'bg-slate-100 text-slate-700', icon: <Building2 size={12} />, text: 'Single' },
            'group_parent': { color: 'bg-purple-100 text-purple-700', icon: <GitBranch size={12} />, text: 'Group' },
            'group_branch': { color: 'bg-indigo-100 text-indigo-700', icon: <GitBranch size={12} />, text: 'Branch' }
        };

        const config = typeConfig[schoolType] || typeConfig['single'];

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                {config.icon}
                {config.text}
            </span>
        );
    };

    const filteredSchools = schools.filter(school =>
        school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (school.domain && school.domain.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Loading state
    if (loading && schools.length === 0) {
        return (
            <Layout pageTitle="Schools Management">
                <div className="min-h-screen p-6 flex items-center justify-center">
                    <div className="text-center">
                        <div
                            className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading schools...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    // Global error state
    if (error && schools.length === 0) {
        return (
            <Layout pageTitle="Schools Management">
                <div className="min-h-screen p-6 flex items-center justify-center">
                    <div className="text-center max-w-md">
                        <div
                            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <XCircle size={32} className="text-red-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">
                            Error Loading Schools
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {error}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => fetchSchools()}
                                className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors mr-3"
                            >
                                Try Again
                            </button>
                            {error.includes('Session expired') && (
                                <button
                                    onClick={() => navigate('/')}
                                    className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    Go to Login
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Schools Management">
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                                <Building2 size={32} className="text-orange-600" />
                                Schools Management
                            </h1>
                            <p className="text-slate-600 mt-1">
                                Manage all schools in your multi-tenant platform
                            </p>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={() => fetchSchools()}
                                className="bg-white text-gray-600 px-4 py-3 rounded-xl font-medium hover:bg-gray-50 transition-all border border-gray-200 flex items-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader size={20} className="animate-spin" />
                                ) : (
                                    <RefreshCw size={20} />
                                )}
                                {loading ? 'Refreshing...' : 'Refresh'}
                            </button>

                            <button
                                onClick={toggleSelectMode}
                                className={`px-4 py-3 rounded-xl font-medium transition-all border flex items-center gap-2 ${
                                    selectMode
                                        ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                }`}
                                title={selectMode ? 'Exit selection mode' : 'Select schools to assign to a group'}
                            >
                                <Layers size={20} />
                                {selectMode ? 'Cancel Selection' : 'Select Schools'}
                            </button>

                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl font-medium hover:from-orange-600 hover:to-orange-700 transition-all duration-200 flex items-center gap-2 shadow-lg"
                            >
                                <Plus size={20} />
                                Add New School
                            </button>
                        </div>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                            <AlertTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-red-800">Error</h3>
                                <p className="text-red-700 text-sm">{error}</p>
                                <button
                                    className="text-red-600 text-sm font-medium mt-2 hover:text-red-700"
                                    onClick={() => setError(null)}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Toast Notification */}
                    {toast.show && <Toast message={toast.message} type={toast.type}
                        onClose={() => setToast({ ...toast, show: false })} />}

                    {/* Search and Filters */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                        <div className="flex flex-col lg:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1 relative">
                                <Search size={20}
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search schools by name or domain..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                />
                            </div>

                            {/* Filters */}
                            <div className="flex gap-3">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="trial">Trial</option>
                                    <option value="suspended">Suspended</option>
                                </select>

                                <select
                                    value={subscriptionFilter}
                                    onChange={(e) => setSubscriptionFilter(e.target.value)}
                                    className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                >
                                    <option value="all">All Plans</option>
                                    <option value="trial">Trial</option>
                                    <option value="basic">Basic</option>
                                    <option value="premium">Premium</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Select-mode info banner */}
                    {selectMode && (
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-purple-700">
                                <GitMerge size={18} />
                                <span className="text-sm font-medium">
                                    Selection mode active — click on any <strong>Single</strong> school card to select it.
                                    {selectedSchools.length > 0 && ` ${selectedSchools.length} selected.`}
                                </span>
                            </div>
                            {selectedSchools.length > 0 && (
                                <button
                                    onClick={() => setSelectedSchools([])}
                                    className="text-xs text-purple-500 hover:text-purple-700 underline flex-shrink-0"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    )}

                    {/* Schools Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredSchools.map((school) => {
                            const isSelected = isSchoolSelected(school.id);
                            const isSelectable = school.school_type === 'single';
                            return (
                            <div
                                key={school.id}
                                className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 relative ${
                                    selectMode && isSelectable
                                        ? isSelected
                                            ? 'border-purple-400 ring-2 ring-purple-200 cursor-pointer'
                                            : 'border-purple-100 hover:border-purple-300 cursor-pointer'
                                        : selectMode && !isSelectable
                                            ? 'border-gray-100 opacity-60'
                                            : 'border-gray-100'
                                }`}
                                onClick={selectMode && isSelectable ? () => toggleSchoolSelection(school) : undefined}
                            >
                                {/* School Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-semibold text-slate-800 mb-1 truncate pr-2">
                                            {school.name}
                                        </h3>
                                        {school.domain && (
                                            <p className="text-sm text-slate-500">
                                                {school.domain}.edumonics.com
                                            </p>
                                        )}
                                    </div>

                                    {/* Checkbox in select mode, dropdown menu otherwise */}
                                    {selectMode ? (
                                        <div
                                            className="flex-shrink-0 ml-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (isSelectable) toggleSchoolSelection(school);
                                            }}
                                        >
                                            {isSelected ? (
                                                <CheckSquare size={22} className="text-purple-500" />
                                            ) : (
                                                <Square
                                                    size={22}
                                                    className={isSelectable ? 'text-gray-400' : 'text-gray-200'}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="relative flex-shrink-0">
                                            <button
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                onClick={() => setSchoolMenuOpen(schoolMenuOpen === school.id ? null : school.id)}
                                            >
                                                <MoreVertical size={16} className="text-gray-400" />
                                            </button>

                                            {/* Dropdown Menu */}
                                            {schoolMenuOpen === school.id && (
                                                <div
                                                    className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg z-10 border border-gray-100">
                                                    <div className="py-1">
                                                        <button
                                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                            onClick={() => {
                                                                setSchoolMenuOpen(null);
                                                                // View action - Implement later
                                                            }}
                                                        >
                                                            <Eye size={14} />
                                                            View Details
                                                        </button>
                                                        <button
                                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                            onClick={() => {
                                                                setSchoolMenuOpen(null);
                                                                handleEditSchool(school);
                                                            }}
                                                        >
                                                            <Edit size={14} />
                                                            Edit School
                                                        </button>
                                                        {/* Assign to Group — only for single-type schools */}
                                                        {school.school_type === 'single' && (
                                                            <button
                                                                className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                                                                onClick={() => handleSingleAssignToGroup(school)}
                                                            >
                                                                <GitMerge size={14} />
                                                                Assign to Group
                                                            </button>
                                                        )}
                                                        <button
                                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                            onClick={() => {
                                                                setSchoolMenuOpen(null);
                                                                setConfirmDeleteSchool(school.id);
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                            Delete School
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Status, Type and Plan */}
                                <div className="flex items-center gap-2 mb-4 flex-wrap">
                                    {getStatusBadge(school.status)}
                                    {getSchoolTypeBadge(school.school_type)}
                                    {getSubscriptionBadge(school.subscription_plan)}
                                </div>

                                {/* School Stats */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-center mb-1">
                                            <Users size={16} className="text-blue-600" />
                                        </div>
                                        <p className="text-sm font-medium text-gray-800">
                                            {school.user_count || 0}
                                        </p>
                                        <p className="text-xs text-gray-500">Users</p>
                                    </div>
                                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-center mb-1">
                                            <Calendar size={16} className="text-green-600" />
                                        </div>
                                        <p className="text-sm font-medium text-gray-800">
                                            {school.created_at ? new Date(school.created_at).toLocaleDateString() : 'N/A'}
                                        </p>
                                        <p className="text-xs text-gray-500">Created</p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2">
                                    <button
                                        className="flex-1 bg-blue-50 text-blue-600 py-2 px-3 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                        onClick={() => {
                                            // View action - Implement later
                                        }}
                                    >
                                        <Eye size={14} />
                                        View
                                    </button>
                                    <button
                                        className="flex-1 bg-gray-50 text-gray-600 py-2 px-3 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                        onClick={() => handleEditSchool(school)}
                                    >
                                        <Edit size={14} />
                                        Edit
                                    </button>

                                    {/* Status Change Button */}
                                    {school.status === 'active' ? (
                                        <button
                                            onClick={() => handleStatusChange(school.id, 'suspended')}
                                            className="bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                            disabled={actionInProgress === school.id}
                                            title="Suspend School"
                                        >
                                            {actionInProgress === school.id ? (
                                                <Loader size={14} className="animate-spin" />
                                            ) : (
                                                <>
                                                    <Pause size={14} />
                                                    <span>Suspend</span>
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleStatusChange(school.id, 'active')}
                                            className="bg-green-50 text-green-600 py-2 px-3 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                            disabled={actionInProgress === school.id}
                                            title="Activate School"
                                        >
                                            {actionInProgress === school.id ? (
                                                <Loader size={14} className="animate-spin" />
                                            ) : (
                                                <>
                                                    <Power size={14} />
                                                    <span>Activate</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>

                                {/* Delete Confirmation Dialog */}
                                {confirmDeleteSchool === school.id && (
                                    <div
                                        className="absolute inset-0 bg-white bg-opacity-95 rounded-2xl flex items-center justify-center p-6 z-20">
                                        <div className="text-center">
                                            <div
                                                className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <Shield size={24} className="text-red-600" />
                                            </div>
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete School?</h3>
                                            <p className="text-sm text-gray-600 mb-4">
                                                This will permanently delete <b>{school.name}</b> and all its data. This
                                                action cannot be undone.
                                            </p>
                                            <div className="flex justify-center gap-3">
                                                <button
                                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                                                    onClick={() => setConfirmDeleteSchool(null)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-1"
                                                    onClick={() => handleDeleteSchool(school.id)}
                                                    disabled={actionInProgress === school.id}
                                                >
                                                    {actionInProgress === school.id ? (
                                                        <>
                                                            <Loader size={14} className="animate-spin" />
                                                            Deleting...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Trash2 size={14} />
                                                            Delete School
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                        })}
                    </div>

                    {/* Empty State */}
                    {!loading && filteredSchools.length === 0 && (
                        <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
                            <Building2 size={64} className="text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-600 mb-2">No schools found</h3>
                            <p className="text-gray-500 mb-6">
                                {searchTerm || statusFilter !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Get started by adding your first school'
                                }
                            </p>

                            {searchTerm || statusFilter !== 'all' ? (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('all');
                                    }}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Clear Filters
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 mx-auto"
                                >
                                    <Plus size={16} />
                                    Add School
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* School Creation Modal */}
            <SchoolCreationModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSchoolCreated={handleSchoolCreated}
            />

            {/* School Edit Modal */}
            <SchoolEditModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSchoolUpdated={handleSchoolUpdated}
                school={selectedSchool}
            />

            {/* Assign to Group Modal */}
            <AssignToGroupModal
                isOpen={showAssignModal}
                onClose={() => {
                    setShowAssignModal(false);
                    setAssignModalSchools([]);
                }}
                onSuccess={handleAssignSuccess}
                schools={assignModalSchools}
            />

            {/* Sticky selection action bar */}
            {selectMode && selectedSchools.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-purple-300 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <CheckSquare size={16} className="text-purple-600" />
                            </div>
                            <span className="font-medium text-slate-800">
                                {selectedSchools.length} school{selectedSchools.length !== 1 ? 's' : ''} selected
                            </span>
                            <span className="hidden sm:block text-sm text-slate-500">
                                — ready to be assigned to a group
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setSelectedSchools([])}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleBulkAssignToGroup}
                                className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-medium hover:from-purple-600 hover:to-purple-700 transition-all flex items-center gap-2 shadow-lg"
                            >
                                <GitMerge size={16} />
                                Assign {selectedSchools.length} School{selectedSchools.length !== 1 ? 's' : ''} to Group
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default SchoolsManagement;